/**
 * Asking for a key we never received.
 *
 * A device can only read sessions that were shared with it, so anything sent
 * before this browser existed is undecryptable until somebody hands the key
 * over. Linking by QR does that up front; signing in with a phone number does
 * not, and without this the history would stay locked forever with no way to
 * recover.
 *
 * `m.room_key_request` is the protocol's self-healing path. The phone already
 * answers it (`handleRoomKeyRequest` in the mobile client) for sessions it
 * owns, replying with an ordinary `m.room_key`.
 *
 * ## One request per session, not per message
 *
 * Opening a conversation with forty unreadable bubbles must not send forty
 * requests. They almost all share a handful of sessions, and the phone would
 * be answering the same question dozens of times over a mobile connection.
 */
import { loadCrypto } from '@nsibi/e2ee-wasm';

import { api } from '../lib/api';
import { getDeviceId } from '../lib/device';
import { getSession } from '../lib/session';
import { encryptOlmTo, queryDevices, type DeviceKey } from './olmSession';
import { MEGOLM_ALGORITHM } from './sessions';
import {
  historyRequested,
  loadMegolmSession,
  loadOutboundSession,
  markHistoryRequested,
} from './store';
import { type RoomKeyRequestContent } from './toDevice';

/** Nsibi-private: "send me everything you can read", to our own phone. */
export const HISTORY_REQUEST_TYPE = 'm.nsibi.history_request';
const FORWARDED_ROOM_KEY_TYPE = 'm.forwarded_room_key';

/** Sessions already asked about this page load. */
const asked = new Set<string>();

export type MissingKey = {
  sessionId: string;
  /** The curve25519 of the device that created the session. */
  senderKey: string;
};

/**
 * Ask the owning device for each missing session key.
 *
 * Requests go to whoever owns `senderKey` - the device that created the
 * session - because that is the only device guaranteed to be able to answer.
 * Returns how many requests were actually sent.
 */
export async function requestMissingKeys(
  channelId: string,
  memberIds: number[],
  missing: MissingKey[],
): Promise<number> {
  const fresh = missing.filter((m) => m.sessionId && m.senderKey && !asked.has(m.sessionId));
  if (fresh.length === 0) return 0;

  const devices = await queryDevices(memberIds);
  const byCurve = new Map(devices.map((d) => [d.curve25519, d]));

  const messages: Array<{
    target_user_id: number;
    target_device_id: string;
    message_type: string;
    ciphertext: string;
  }> = [];

  for (const miss of fresh) {
    const owner = byCurve.get(miss.senderKey);
    if (!owner) continue; // sender device not in the directory; nothing to ask

    const ciphertext = await encryptOlmTo(owner, {
      type: 'm.room_key_request',
      content: {
        action: 'request',
        algorithm: MEGOLM_ALGORITHM,
        room_id: channelId,
        session_id: miss.sessionId,
        sender_key: miss.senderKey,
        requesting_device_id: getDeviceId(),
      },
    });
    if (!ciphertext) continue;

    // Mark asked even though the answer may never come. A request that goes
    // unanswered is not a reason to ask again every time the conversation is
    // opened; a reload clears this and tries once more.
    asked.add(miss.sessionId);
    messages.push({
      target_user_id: owner.userId,
      target_device_id: owner.deviceId,
      message_type: 'm.room_key_request',
      ciphertext,
    });
  }

  if (messages.length === 0) return 0;
  await api('/v1/sendToDevice/', {
    method: 'POST',
    body: { sender_device_id: getDeviceId(), messages },
  });
  return messages.length;
}


/**
 * Ask the phone for its history, once.
 *
 * Linking by QR cannot hand history over at scan time: approval is what
 * gives this browser a token, and it cannot publish keys - cannot be sealed
 * to - until it has one. The phone now waits for that publish and forwards
 * its sessions while the scanner is still on screen, but that depends on the
 * person holding the phone open for a few seconds. This is the path that
 * does not: it runs right after this browser first publishes, reaches the
 * phone on its next mailbox drain (the server nudges it), and the phone
 * answers with every session it holds. Idempotent on the phone, so asking
 * after a hand-off already happened costs one batch of duplicate keys the
 * importer ignores.
 *
 * Only our own account's devices are asked. Returns how many were.
 */
export async function requestHistoryFromOwnDevices(force = false): Promise<number> {
  if (!force && (await historyRequested())) return 0;
  const me = getSession()?.user.id;
  if (!me) return 0;

  const myDeviceId = getDeviceId();
  const mine = (await queryDevices([me])).filter((d) => d.deviceId !== myDeviceId);
  if (mine.length === 0) return 0;

  const messages = [];
  for (const device of mine) {
    const ciphertext = await encryptOlmTo(device, {
      type: HISTORY_REQUEST_TYPE,
      content: { requesting_device_id: myDeviceId },
    });
    if (!ciphertext) continue;
    messages.push({
      target_user_id: device.userId,
      target_device_id: device.deviceId,
      message_type: HISTORY_REQUEST_TYPE,
      ciphertext,
    });
  }
  if (messages.length === 0) return 0;

  await api('/v1/sendToDevice/', {
    method: 'POST',
    body: { sender_device_id: myDeviceId, messages },
  });
  await markHistoryRequested();
  return messages.length;
}

/**
 * Answer a key request for a session THIS browser created.
 *
 * The mirror of `requestMissingKeys`: when the phone - or a peer - could not
 * read something we sent, it asks the owner of the session's `sender_key`,
 * which is us. Without an answer a message sent from the web whose key
 * fan-out missed a device stays locked on that device forever; the phone has
 * had this responder for a while, the browser had none.
 *
 * Rules: the request must name a session we hold the outbound for in that
 * room, and the requester must be our own account or a current member. The
 * key goes out in EXPORTED form under `m.forwarded_room_key`, which is what
 * exporting our inbound copy produces and what both importers accept.
 */
export async function answerKeyRequest(
  content: RoomKeyRequestContent,
  from: { userId: number; deviceId: string },
): Promise<void> {
  if (content.action !== 'request') return;
  const roomId = content.room_id ?? '';
  const sessionId = content.session_id ?? '';
  if (!roomId || !sessionId) return;

  const me = getSession()?.user.id;
  if (!me) return;
  const fromOwnAccount = from.userId === me;

  // Answer for ANY session we actually hold when the requester is our own
  // device (a linked browser / second device). Requiring the current outbound
  // row to still match refused a valid request the moment the session rotated,
  // which is exactly the "waiting for this message" that never healed. For
  // another user we keep the forward-secrecy gate: only sessions we shared
  // with them, and only ones we created (the outbound row).
  if (!fromOwnAccount) {
    const outbound = await loadOutboundSession(roomId);
    if (!outbound || outbound.sessionId !== sessionId) return;
    if (!outbound.sharedWith.includes(`${from.userId}:${from.deviceId}`)) return;
  }

  let requester: DeviceKey | undefined;
  if (fromOwnAccount) {
    requester = (await queryDevices([me])).find((d) => d.deviceId === from.deviceId);
  } else {
    requester = (await queryDevices([from.userId])).find((d) => d.deviceId === from.deviceId);
  }
  if (!requester) {
    console.warn(`[nsibi] key request: requester device ${from.deviceId.slice(0,8)} not found`);
    return;
  }

  const pickle = await loadMegolmSession(sessionId);
  if (!pickle) {
    console.warn(`[nsibi] key request: no session ${sessionId.slice(0,10)} held to answer`);
    return;
  }
  const core = await loadCrypto();
  let exported: string;
  try {
    exported = core.exportInboundGroupSession(pickle);
  } catch {
    return;
  }

  const ciphertext = await encryptOlmTo(requester, {
    type: FORWARDED_ROOM_KEY_TYPE,
    content: {
      algorithm: MEGOLM_ALGORITHM,
      room_id: roomId,
      session_id: sessionId,
      session_key: exported,
      sender_key: content.sender_key ?? '',
    },
  });
  if (!ciphertext) return;

  await api('/v1/sendToDevice/', {
    method: 'POST',
    body: {
      sender_device_id: getDeviceId(),
      messages: [
        {
          target_user_id: requester.userId,
          target_device_id: requester.deviceId,
          message_type: FORWARDED_ROOM_KEY_TYPE,
          ciphertext,
        },
      ],
    },
  });
  console.log(
    `[nsibi] answered key request for ${sessionId.slice(0, 10)} -> ${requester.deviceId.slice(0, 8)}`,
  );
}
